import { Box, Checkbox, CheckboxGroup } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { find, filter, includes, map, findIndex } from 'lodash';

import { Condition, Iteration } from 'Component/others';
import Tooltip from 'UiKit/tooltip';
import { TextRegular, TextSemiBold } from 'UiKit/typography';
import ButtonLink from 'UiKit/button/buttonLink';

import styles from './styles.module.css';

const CheckboxAccess = ({ setValue, menuList, operatorMenu }) => {
  const { t } = useTranslation('role');
  const updatePermissions = useCallback(
    (id, permissions, remove = false) => {
      const newMenus = remove
        ? filter(operatorMenu, (menu) => menu.id !== id)
        : (() => {
            const index = findIndex(operatorMenu, { id });
            const updated = { id, permissions };
            return index === -1 ? [...operatorMenu, updated] : operatorMenu.map((menu, i) => (i === index ? updated : menu));
          })();

      setValue('operatorMenu', newMenus);
    },
    [operatorMenu, setValue]
  );

  const handleCheckMenu = useCallback(
    (values, id) => {
      const parsedValues = map(values, Number);
      const permissionList = find(menuList, { id })?.permissions || [];
      const readPermission = find(permissionList, { attribute: 'read' });
      const readId = Number(readPermission?.id);
      const hasRead = includes(parsedValues, readId);

      updatePermissions(id, parsedValues, !hasRead);
    },
    [menuList, updatePermissions]
  );

  const handleSelectAll = useCallback(
    (id, allPermissionIds) => {
      const index = findIndex(operatorMenu, { id });
      const isFullySelected = index !== -1 && operatorMenu[index].permissions.length === allPermissionIds.length;

      updatePermissions(id, allPermissionIds, isFullySelected);
    },
    [operatorMenu, updatePermissions]
  );

  return (
    <Box className={styles['checkboxes-access__container']}>
      <Iteration
        items={menuList}
        render={(item) => {
          const menuIndex = findIndex(operatorMenu, { id: item.id });
          const selectedPermissions = menuIndex !== -1 ? operatorMenu[menuIndex].permissions : [];
          const allPermissionIds = map(item.permissions, (p) => Number(p.id));
          const readPermission = find(item.permissions, { attribute: 'read' });
          const readId = Number(readPermission?.id);
          const isSelectedAll = selectedPermissions.length === allPermissionIds.length;

          return (
            <Box key={item.id} className={styles['checkboxes-access__checkbox-container']}>
              <Box className={styles['checkboxes-access__checkbox-title']}>
                <TextSemiBold className={styles['checkboxes-access__checkbox-title-text']}>{item.name || '-'}</TextSemiBold>
              </Box>

              <Box className={styles['checkboxes-access__checkbox-body']}>
                <Box className={styles['checkboxes-access__checkbox-group']}>
                  <CheckboxGroup value={selectedPermissions} onChange={(val) => handleCheckMenu(val, item.id)}>
                    <Iteration
                      items={item.permissions}
                      render={(permission) => {
                        const isDisabled = permission.attribute !== 'read' && !includes(selectedPermissions, readId);

                        return (
                          <Checkbox key={permission.id} value={permission.id} disabled={isDisabled}>
                            <Tooltip label={t('checkboxAccessTooltip')} hasArrow isDisabled={Number(permission.id) === readId}>
                              <TextRegular className={styles['checkboxes-access__checkbox-text']}>{permission.name}</TextRegular>
                            </Tooltip>
                          </Checkbox>
                        );
                      }}
                    />
                  </CheckboxGroup>
                </Box>

                <ButtonLink onClick={() => handleSelectAll(item.id, allPermissionIds)}>
                  <TextSemiBold className={styles['checkboxes-access__button-select-all-text']}>
                    <Condition statement={isSelectedAll} fallback={t('selectAll')}>
                      {t('deselectAll')}
                    </Condition>
                  </TextSemiBold>
                </ButtonLink>
              </Box>
            </Box>
          );
        }}
      />
    </Box>
  );
};

const MemoizedCheckboxAccess = ({ menuList, form }) => {
  const { setValue, watch } = form;
  const { operatorMenu } = watch();

  return useMemo(() => <CheckboxAccess setValue={setValue} menuList={menuList} operatorMenu={operatorMenu} />, [setValue, menuList, operatorMenu]);
};

export default MemoizedCheckboxAccess;